import * as React from 'react';
import { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router';
import { attachmentsUrl } from '../../AppSettings';
import {
    Box,
    Card,
    Button,
    Container,
    Divider,
    Grid,
    Typography,
    InputLabel,
    MenuItem,
    Select,
    FormControl,
    FormControlLabel,
    Switch,
    TextField,
    Snackbar,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Link,
    Alert,
} from '@mui/material';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import { Title } from '../../components/Title';
import {
    CategoryData,
    QuestionData,
    getQuestionForEdit,
    getCategories,
    putQuestion,
    postQuestion,
    deleteQuestion,
    uploadAttachment,
} from '../../data';
import { Editor } from '@tinymce/tinymce-react';
import { useForm, Controller, SubmitHandler } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { tinymceKey } from '../../AppSettings';
import { accountService, UserRole } from '../../account.service';
import { Navigate } from 'react-router-dom';

type IFormData = {
    categoryId: number;
    title: string;
    labels: string;
    content: string;
    isPublic: boolean;
    isPublished: boolean;
    isArchived: boolean;
    isPromoted: boolean;
};

interface IToastState {
    open: boolean;
    status: 'error' | 'info' | 'success' | 'warning';
    message: string;
}

interface IAttachment {
    id: number;
    fileName: string;
    add: boolean;
    remove: boolean;
}

export const QuestionDetails = () => {
    const { questionId } = useParams();
    const navigate = useNavigate();
    const account = accountService.getAccount();
    const [categories, setCategories] = useState<CategoryData[]>([]);
    const [question, setQuestion] = useState<QuestionData | null>(null);
    const [openDialog, setOpenDialog] = useState(false);
    const [updated, setUpdated] = useState<Date>(null);
    const editorRef = useRef(null);
    const [toastState, setToastState] = useState<IToastState>({
        open: false,
        status: 'success',
        message: '',
    });
    const [attachments, setAttachments] = useState<IAttachment[]>([]);

    useEffect(() => {
        let cancelled = false;
        const doGetCategories = async () => {
            const fetchedCategories = await getCategories();
            if (!cancelled) {
                setCategories(fetchedCategories);
            }
        };
        doGetCategories();
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        let cancelled = false;
        const doGetQuestion = async (questionId: number) => {
            const fetchedQuestion = await getQuestionForEdit(questionId);
            if (!cancelled) {
                setQuestion(fetchedQuestion);
                setUpdated(fetchedQuestion.updated);
            }
        };
        if (questionId) {
            doGetQuestion(Number(questionId));
        } else {
            setQuestion({
                questionId: 0,
                categoryId: 0,
                categoryName: '',
                title: '',
                labels: '',
                updated: new Date(),
                content: '',
                isPublic: true,
                isPublished: false,
                isArchived: false,
                isPromoted: false,
            });
        }
        return () => {
            cancelled = true;
        };
    }, [questionId]);

    const { handleSubmit, control } = useForm<IFormData>();
    const submitHandler: SubmitHandler<IFormData> = async (data) => {
        let state: IToastState;
        let content = '';
        if (editorRef.current) {
            content = editorRef.current.getContent();
            if (content.length === 0) {
                state = { open: true, status: 'error', message: 'Article content cannot be empty' };
                setToastState(state);
                return;
            }
        }
        if (question.questionId === 0) {
            const result = await postQuestion({
                categoryId: Number(data.categoryId),
                title: data.title,
                content: content,
                labels: data.labels,
                isPublic: data.isPublic,
                isPublished: data.isPublished,
                isArchived: data.isArchived,
                isPromoted: data.isPromoted,
                userId: 1,
            });
            if (result) {
                let attachmentsToAdd = '';
                for (let i = 0; i < attachments.length; i++) {
                    if (attachments[i].add) {
                        if (attachmentsToAdd.length > 0) {
                            attachmentsToAdd += ',';
                        }
                        attachmentsToAdd += attachments[i].id.toString();
                    }
                }

                // await addAttachments(result.questionId, { attachments: attachmentsToAdd });

                state = { open: true, status: 'success', message: 'Article was successfully created' };
                setUpdated(result.updated);
            } else {
                state = { open: true, status: 'error', message: 'Failed to create article' };
            }
        } else {
            const result = await putQuestion(Number(questionId), {
                categoryId: Number(data.categoryId),
                title: data.title,
                content: content,
                labels: data.labels,
                isPublic: data.isPublic,
                isPublished: data.isPublished,
                isArchived: data.isArchived,
                isPromoted: data.isPromoted,
                userId: 1,
            });
            if (result) {
                state = { open: true, status: 'success', message: 'Article was successfully updated' };
                setUpdated(result.updated);
            } else {
                state = { open: true, status: 'error', message: 'Failed to save article' };
            }
        }
        setToastState(state);
    };

    const handleCloseSnackbar = (event?: React.SyntheticEvent, reason?: string) => {
        if (reason === 'clickaway') {
            return;
        }
        setToastState({ ...toastState, open: false });
    };

    const handleDelete = async () => {
        setOpenDialog(false);
        const result = await deleteQuestion(Number(questionId));

        if (!result) {
            let state: IToastState;
            state = { open: true, status: 'error', message: 'Failed to delete article' };
            setToastState(state);
        } else {
            navigate('/kb');
        }
    };

    // file upload
    const hiddenFileInput = React.useRef(null);
    const handleUploadClick = (event) => {
        hiddenFileInput.current.click();
    };
    const handleUploadChange = (event) => {
        const file = event.target.files[0];
        if (file !== undefined) {
            const doUpload = async () => {
                const attachment = await uploadAttachment(file);
                if (attachment === undefined) {
                    let state: IToastState;
                    state = { open: true, status: 'error', message: `Failed to upload ${file.name}` };
                    setToastState(state);
                } else {
                    setAttachments([
                        ...attachments,
                        { id: attachment.attachmentId, fileName: attachment.fileName, add: true, remove: false },
                    ]);
                }
            };
            doUpload();
        }
    };

    return (
        <>
            {(!account || account.role !== UserRole.Admin) && <Navigate to="/account/login" />}
            {question && <Title title={question.title} />}
            <Box
                sx={{
                    backgroundColor: 'background.default',
                    minHeight: '100%',
                    py: 3,
                }}
            >
                <Dialog
                    open={openDialog}
                    onClose={() => setOpenDialog(false)}
                    aria-labelledby="alert-dialog-title"
                    aria-describedby="alert-dialog-description"
                >
                    <DialogTitle id="alert-dialog-title">{'Delete this article?'}</DialogTitle>
                    <DialogContent>
                        <DialogContentText id="alert-dialog-description">
                            Are you sure you want to delete this article?
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
                        <Button onClick={handleDelete} autoFocus>
                            ОК
                        </Button>
                    </DialogActions>
                </Dialog>
                {question && (
                    <Container maxWidth={false}>
                        <Snackbar
                            open={toastState.open}
                            autoHideDuration={6000}
                            onClose={handleCloseSnackbar}
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        >
                            <Alert onClose={handleCloseSnackbar} severity={toastState.status}>
                                {toastState.message}
                            </Alert>
                        </Snackbar>

                        <Grid container justifyContent="space-between" spacing={3}>
                            <Grid item lg={8} xl={9} xs={12}>
                                <Card sx={{ p: 3 }}>
                                    <Box
                                        sx={{
                                            m: 0,
                                            mb: 4,
                                            maxWidth: '100%',
                                            fontSize: 20,
                                        }}
                                    >
                                        <Controller
                                            name="title"
                                            control={control}
                                            defaultValue={question.title}
                                            rules={{
                                                required: 'Title is required',
                                                maxLength: {
                                                    value: 4000,
                                                    message: 'Title cannot exceed 4000 characters',
                                                },
                                            }}
                                            render={({ field, fieldState: { error } }) => (
                                                <TextField
                                                    {...field}
                                                    fullWidth
                                                    label="Title"
                                                    error={!!error}
                                                    helperText={error ? error.message : null}
                                                />
                                            )}
                                        />

                                        <Typography variant="overline">
                                            Last Updated {updated && updated.toLocaleDateString()}{' '}
                                            {updated && updated.toLocaleTimeString()}
                                        </Typography>
                                    </Box>
                                    <Editor
                                        onInit={(evt, editor) => (editorRef.current = editor)}
                                        apiKey={tinymceKey}
                                        initialValue={question.content}
                                        init={{
                                            height: 500,
                                            menubar: false,
                                            plugins: [
                                                'advlist autolink lists link image charmap print preview anchor',
                                                'searchreplace visualblocks code fullscreen',
                                                'insertdatetime media table paste code help wordcount',
                                                'imagetools code link',
                                            ],
                                            toolbar:
                                                'undo redo | formatselect | bold italic forecolor | alignleft aligncenter | bullist numlist outdent indent | fullscreen removeformat | link image | code | table tabledelete | tableprops tablerowprops tablecellprops | tableinsertrowbefore tableinsertrowafter tabledeleterow | tableinsertcolbefore tableinsertcolafter tabledeletecol',
                                        }}
                                        onEditorChange={(content, editor) => {}}
                                    />
                                </Card>
                            </Grid>
                            <Grid item lg={4} xl={3} xs={12}>
                                <Box
                                    sx={{
                                        backgroundColor: 'background.default',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        flexGrow: 1,
                                    }}
                                >
                                    <Box
                                        sx={{
                                            flexGrow: 1,
                                            overflow: 'auto',
                                        }}
                                    >
                                        <Grid container direction="column" spacing="" sx={{ p: 3 }}>
                                            <Grid item>
                                                <form onSubmit={handleSubmit(submitHandler)}>
                                                    <FormControl fullWidth>
                                                        <Button color="primary" variant="contained" type="submit">
                                                            Save
                                                        </Button>
                                                    </FormControl>
                                                </form>
                                            </Grid>
                                            <Grid item sx={{ mt: 3 }}>
                                                <Controller
                                                    name="isPublished"
                                                    control={control}
                                                    defaultValue={question.isPublished}
                                                    render={({ field }) => (
                                                        <FormControlLabel
                                                            control={
                                                                <Switch
                                                                    {...field}
                                                                    color="primary"
                                                                    defaultChecked={question.isPublished}
                                                                />
                                                            }
                                                            label="Published"
                                                        />
                                                    )}
                                                />
                                            </Grid>
                                            <Grid item sx={{ mt: 0 }}>
                                                <Controller
                                                    name="isPublic"
                                                    control={control}
                                                    defaultValue={question.isPublic}
                                                    render={({ field }) => (
                                                        <FormControlLabel
                                                            control={
                                                                <Switch
                                                                    {...field}
                                                                    color="primary"
                                                                    defaultChecked={question.isPublic}
                                                                />
                                                            }
                                                            label="Public"
                                                        />
                                                    )}
                                                />
                                            </Grid>
                                            <Grid item sx={{ mt: 2 }}>
                                                <FormControl fullWidth>
                                                    <InputLabel htmlFor="category-select">Category</InputLabel>
                                                    <Controller
                                                        control={control}
                                                        name="categoryId"
                                                        defaultValue={question.categoryId ?? 0}
                                                        render={({ field }) => (
                                                            <Select {...field} id="category-select">
                                                                <MenuItem key={0} value={0} disabled>
                                                                    NO CATEGORY
                                                                </MenuItem>
                                                                {categories.map((category) => (
                                                                    <MenuItem
                                                                        key={category.categoryId}
                                                                        value={category.categoryId}
                                                                    >
                                                                        {category.name}
                                                                    </MenuItem>
                                                                ))}
                                                            </Select>
                                                        )}
                                                    />
                                                </FormControl>
                                            </Grid>
                                            <Grid item sx={{ mt: 4 }}>
                                                <Controller
                                                    name="labels"
                                                    control={control}
                                                    defaultValue={question.labels}
                                                    rules={{
                                                        maxLength: {
                                                            value: 1000,
                                                            message: 'Labels cannot exceed 1000 characters',
                                                        },
                                                    }}
                                                    render={({ field, fieldState: { error } }) => (
                                                        <TextField
                                                            {...field}
                                                            fullWidth
                                                            label="Labels"
                                                            variant="outlined"
                                                            error={!!error}
                                                            helperText={error ? error.message : null}
                                                        />
                                                    )}
                                                />
                                            </Grid>

                                            <Grid item sx={{ mt: 2 }}>
                                                <Controller
                                                    name="isPromoted"
                                                    control={control}
                                                    defaultValue={question.isPromoted}
                                                    render={({ field }) => (
                                                        <FormControlLabel
                                                            control={
                                                                <Switch
                                                                    {...field}
                                                                    color="primary"
                                                                    defaultChecked={question.isPromoted}
                                                                />
                                                            }
                                                            label="Promoted"
                                                        />
                                                    )}
                                                />
                                            </Grid>

                                            <Grid item sx={{ mt: 2 }}>
                                                <Typography color="textSecondary" variant="subtitle2" sx={{ mb: 1 }}>
                                                    Attachments
                                                </Typography>
                                                {attachments.map((attach) => (
                                                    <div key={attach.id}>
                                                        <Link
                                                            variant="body2"
                                                            underline="hover"
                                                            href={attachmentsUrl + attach.id + '_' + attach.fileName}
                                                            target="_blank"
                                                        >
                                                            {attach.fileName}
                                                        </Link>
                                                        <Link
                                                            component="button"
                                                            variant="body2"
                                                            color="secondary"
                                                            onClick={() => {}}
                                                        >
                                                            <DeleteForeverIcon />
                                                        </Link>
                                                    </div>
                                                ))}
                                                <FormControl>
                                                    <Button
                                                        color="primary"
                                                        variant="text"
                                                        onClick={handleUploadClick}
                                                        sx={{ mt: 1 }}
                                                    >
                                                        Upload attachment
                                                    </Button>
                                                    <input
                                                        type="file"
                                                        ref={hiddenFileInput}
                                                        onChange={handleUploadChange}
                                                        style={{ display: 'none' }}
                                                    />
                                                </FormControl>
                                            </Grid>

                                            <Grid item sx={{ mt: 2, pt: 1 }}>
                                                <Divider />
                                                <Controller
                                                    name="isArchived"
                                                    control={control}
                                                    defaultValue={question.isArchived}
                                                    render={({ field }) => (
                                                        <FormControlLabel
                                                            sx={{ my: 2 }}
                                                            control={
                                                                <Switch
                                                                    {...field}
                                                                    color="secondary"
                                                                    defaultChecked={question.isArchived}
                                                                />
                                                            }
                                                            label="Archived"
                                                        />
                                                    )}
                                                />
                                            </Grid>

                                            {question.questionId !== 0 && (
                                                <Grid item>
                                                    <Button color="secondary" onClick={() => setOpenDialog(true)}>
                                                        Delete article
                                                    </Button>
                                                </Grid>
                                            )}
                                        </Grid>
                                    </Box>
                                </Box>
                            </Grid>
                        </Grid>
                    </Container>
                    // </form>
                )}
            </Box>
        </>
    );
};
